import React from 'react'
import {CampaignChannel, ChannelVariant} from '../../../types/campaigns'
import {WhatsAppPreview} from './tiptap/WhatsAppPreview'

interface VariantPreviewProps {
  variant: ChannelVariant
  variables?: Record<string, string>
  liveBodyText?: string
}

const CHANNEL_ICONS: Record<CampaignChannel, string> = {
  SMS: '💬',
  EMAIL: '📧',
  WHATSAPP: '📱',
  TELEGRAM: '✈️',
}

const CHANNEL_COLORS: Record<CampaignChannel, string> = {
  SMS: '#6c757d',
  EMAIL: '#0d6efd',
  WHATSAPP: '#25D366',
  TELEGRAM: '#0088cc',
}

export const VariantPreview: React.FC<VariantPreviewProps> = ({variant, variables = {}, liveBodyText}) => {
  // Replace variables in text
  const replaceVariables = (text: string) => {
    let result = text || ''
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value)
    })
    return result
  }

  const stripHtml = (html?: string) => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').trim()
  }

  // Use live text if provided, otherwise use saved variant text
  const bodyTextToShow =
    liveBodyText !== undefined && liveBodyText !== null && liveBodyText !== ''
      ? liveBodyText
      : variant.bodyText || stripHtml(variant.bodyHtml)

  const renderContent = () => {
    const channelColor = CHANNEL_COLORS[variant.channel]

    switch (variant.channel) {
      case 'SMS':
        return (
          <div className='border rounded p-3' style={{backgroundColor: '#f8f9fa'}}>
            <div className='d-flex align-items-start'>
              <div
                className='badge me-2'
                style={{backgroundColor: channelColor, fontSize: '16px'}}
              >
                {CHANNEL_ICONS.SMS}
              </div>
              <div className='flex-grow-1'>
                <div
                  className='alert alert-secondary mb-0'
                  style={{
                    maxWidth: '300px',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {replaceVariables(bodyTextToShow)}
                </div>
                <small className='text-muted'>
                  {bodyTextToShow.length} caracteres
                  {variant.smsMaxLength && ` / ${variant.smsMaxLength} max`}
                </small>
              </div>
            </div>
          </div>
        )

      case 'EMAIL':
        return (
          <div className='border rounded p-3' style={{backgroundColor: '#f8f9fa'}}>
            {/* Email header */}
            <div className='mb-3 pb-2 border-bottom'>
              <div className='d-flex align-items-center mb-2'>
                <span className='me-2' style={{fontSize: '20px'}}>
                  {CHANNEL_ICONS.EMAIL}
                </span>
                <strong>Asunto:</strong>
                <span className='ms-2'>{replaceVariables(variant.subject)}</span>
              </div>
              <small className='text-muted'>De: noreply@policytools.com</small>
            </div>

            {/* Email body */}
            <div className='bg-white p-3 rounded border'>
              {variant.bodyHtml ? (
                <div dangerouslySetInnerHTML={{__html: replaceVariables(variant.bodyHtml)}} />
              ) : (
                <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>
                  {replaceVariables(bodyTextToShow)}
                </pre>
              )}
            </div>

            {/* Unsubscribe footer */}
            <div className='mt-3 pt-2 border-top text-center'>
              <small className='text-muted'>
                <a href='#' className='text-muted'>
                  Cancelar suscripción
                </a>
              </small>
            </div>
          </div>
        )

      case 'WHATSAPP':
        if (!bodyTextToShow?.trim()) {
          return (
            <div className='alert alert-warning'>
              Agrega texto en la variante para ver la vista previa de WhatsApp.
            </div>
          )
        }

        return (
          <div>
            <WhatsAppPreview message={replaceVariables(bodyTextToShow)} />
            {variant.whatsappTemplateId && (
              <div className='alert alert-info mt-3 small'>
                <strong>Template ID:</strong> {variant.whatsappTemplateId} ({variant.whatsappLanguage})
              </div>
            )}
          </div>
        )

      case 'TELEGRAM':
        return (
          <div className='border rounded p-3' style={{backgroundColor: '#f0f2f5'}}>
            <div className='d-flex align-items-start'>
              <div
                className='badge me-2'
                style={{backgroundColor: channelColor, fontSize: '16px'}}
              >
                {CHANNEL_ICONS.TELEGRAM}
              </div>
              <div className='flex-grow-1'>
                <div
                  className='bg-white rounded p-3 shadow-sm'
                  style={{
                    maxWidth: '350px',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {replaceVariables(bodyTextToShow)}
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h4 className='card-title mb-0'>
          Vista Previa - {variant.channel}
        </h4>
      </div>
      <div className='card-body'>{renderContent()}</div>
    </div>
  )
}
